import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ConfirmInfoModal from "../components/ConfirmInfoModal";
import Cookies from "js-cookie";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 동이 문자로 고정되는 단지 (표시 시 「동」 접미사 추가)
const FIXED_BUILDING_BY_APARTMENT = {
  ForeonStation5: "스테이션5",
};

function KakaoLoginPage() {
  const navigate = useNavigate();
  const apartmentCode = Cookies.get("apartmentCode") || "";
  const fixedBuilding = FIXED_BUILDING_BY_APARTMENT[apartmentCode] || "";
  const hasFixedBuilding = Boolean(fixedBuilding);
  const unitMaxLength = apartmentCode === "ForeonStation5" ? 30 : 5;

  const [formData, setFormData] = useState({
    building: fixedBuilding || "",
    unit: "",
    applicantName: "",
  });

  const [errors, setErrors] = useState({
    applicantName: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 모달 상태 변경 감지
  useEffect(() => {}, [showConfirmModal]);

  // 고정 동 단지인 경우 동 값 유지
  useEffect(() => {
    if (fixedBuilding) {
      setFormData((prev) => ({
        ...prev,
        building: fixedBuilding,
      }));
    }
  }, [fixedBuilding]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // 고정 동 단지에서는 동 입력 무시
    if (name === "building" && hasFixedBuilding) {
      return;
    }

    // 동·호수 글자 수 제한 (포레온스테이션5 호수는 30자, 한글 제외)
    if (name === "building" || name === "unit") {
      const withoutHangul =
        name === "unit" && apartmentCode === "ForeonStation5"
          ? value.replace(/[ㄱ-ㅎㅏ-ㅣ가-힣]/g, "")
          : value;
      const limit = name === "unit" ? unitMaxLength : 5;
      const limitedValue = withoutHangul.slice(0, limit);
      setFormData((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleKakaoLogin = () => {
    // 에러 메시지 초기화
    setErrors({
      applicantName: "",
    });

    // 필수값 검증
    if (
      !formData.building.trim() ||
      !formData.unit.trim() ||
      !formData.applicantName.trim()
    ) {
      alert("모든 필수 항목을 입력해주세요.");
      return;
    }

    // 신청인명 유효성 검증 (자음/모음만 있는 경우 체크)
    const name = formData.applicantName.trim();

    // 완성된 한글이 아닌 자음/모음이 포함되어 있는지 체크
    // 완성된 한글(가-힣)이 아닌 자음(ㄱ-ㅎ)이나 모음(ㅏ-ㅣ)이 있으면 에러
    const hasIncompleteHangul = /[ㄱ-ㅎㅏ-ㅣ]/.test(name);

    if (hasIncompleteHangul) {
      setErrors((prev) => ({
        ...prev,
        applicantName: "이름을 정확히 입력해주세요.",
      }));
      return;
    }

    // 확인 모달 표시
    setShowConfirmModal(true);
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmModalConfirm = () => {
    // 모달 닫기
    setShowConfirmModal(false);

    // 동, 호수 정보를 쿠키에 저장
    const buildingUnitInfo = {
      building: formData.building.trim(),
      unit: formData.unit.trim(),
      applicantName: formData.applicantName.trim(),
    };

    Cookies.set("buildingUnitInfo", JSON.stringify(buildingUnitInfo), {
      expires: 1, // 1일 후 만료
      path: "/",
    });

    // 카카오 소셜 로그인 URL로 이동
    const activeChatId = "kakao_login"; // 상태 식별자
    // const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=773567d2c88e5caba57b5acb024de423&redirect_uri=${encodeURIComponent(
    //   "http://localhost:3000/co-owner-check",
    // const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=773567d2c88e5caba57b5acb024de423&redirect_uri=${encodeURIComponent(
    //   "http://172.30.1.33:3000/co-owner-check"
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=773567d2c88e5caba57b5acb024de423&redirect_uri=${encodeURIComponent(
      "https://www.theh-agreement.co.kr/co-owner-check",
    )}&response_type=code&state=${activeChatId}`;

    window.location.href = kakaoAuthUrl;
  };

  return (
    <motion.div
      className="kakao-login-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        height: "100vh",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        padding: "40px 24px 24px 24px",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      {/* 메인 콘텐츠 영역 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          textAlign: "center",
          paddingTop: "0px",
          paddingBottom: "200px",
        }}
      >
        {/* 헤더 */}
        <motion.div
          style={{
            marginTop: "-20px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.1,
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1],
            },
          }}
        >
          <button
            onClick={() => navigate("/")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 style={{ margin: 0, flex: 1, textAlign: "center" }}>세대 확인</h1>
          <div style={{ width: "40px" }}></div>
        </motion.div>

        {/* 폼 영역 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginTop: "60px",
          }}
        >
          {/* 동 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              동<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              inputMode={hasFixedBuilding ? "text" : "numeric"}
              name="building"
              value={
                hasFixedBuilding
                  ? formatBuildingLabel(fixedBuilding, apartmentCode)
                  : formData.building
              }
              onChange={handleInputChange}
              placeholder="예: 102"
              maxLength={hasFixedBuilding ? 20 : 5}
              readOnly={hasFixedBuilding}
              disabled={hasFixedBuilding}
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: hasFixedBuilding ? "#F9FAFB" : "#FFFFFF",
                color: hasFixedBuilding ? "#6B7280" : "#111827",
                boxSizing: "border-box",
                outline: "none",
                cursor: hasFixedBuilding ? "not-allowed" : "text",
              }}
              onFocus={(e) => {
                if (hasFixedBuilding) return;
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
            {hasFixedBuilding && (
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "13px",
                  color: "#6B7280",
                  textAlign: "left",
                }}
              >
                이 단지는 동이{" "}
                {formatBuildingLabel(fixedBuilding, apartmentCode)}로
                고정됩니다.
              </p>
            )}
          </div>

          {/* 호수 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              호수<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              inputMode={apartmentCode === "ForeonStation5" ? "text" : "numeric"}
              name="unit"
              value={formData.unit}
              onChange={handleInputChange}
              placeholder={
                apartmentCode === "ForeonStation5" ? "예: A1001" : "예: 1001"
              }
              maxLength={unitMaxLength}
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: "#FFFFFF",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
          </div>

          {/* 신청인 명 입력 필드 */}
          <div style={{ width: "100%" }}>
            <label
              style={{
                display: "block",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000000",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              신청인 명<span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              type="text"
              name="applicantName"
              value={formData.applicantName}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  applicantName: value,
                }));
                // 에러 메시지 초기화
                if (errors.applicantName) {
                  setErrors((prev) => ({
                    ...prev,
                    applicantName: "",
                  }));
                }
              }}
              placeholder="예: 홍길동"
              style={{
                width: "100%",
                height: "48px",
                padding: "0 16px",
                border: "2px solid #F3F4F6",
                borderRadius: "8px",
                fontSize: "16px",
                backgroundColor: "#FFFFFF",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7A4FFF";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#F3F4F6";
                e.target.style.outline = "none";
              }}
            />
            {errors.applicantName && (
              <div
                style={{
                  color: "#FF0000",
                  fontSize: "14px",
                  marginTop: "4px",
                  textAlign: "left",
                }}
              >
                {errors.applicantName}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* 하단 카카오 로그인 버튼 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        style={{
          position: "fixed",
          bottom: 35,
          left: 0,
          right: 0,
          padding: "24px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "env(safe-area-inset-bottom, 40px)",
        }}
      >
        <button
          onClick={handleKakaoLogin}
          disabled={
            !formData.building.trim() ||
            !formData.unit.trim() ||
            !formData.applicantName.trim()
          }
          style={{
            width: "100%",
            height: "56px",
            backgroundColor:
              !formData.building.trim() ||
              !formData.unit.trim() ||
              !formData.applicantName.trim()
                ? "#F3F4F6"
                : "#FEE500",
            color: "#000000",
            border: "none",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            cursor:
              !formData.building.trim() ||
              !formData.unit.trim() ||
              !formData.applicantName.trim()
                ? "not-allowed"
                : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            if (
              formData.building.trim() &&
              formData.unit.trim() &&
              formData.applicantName.trim()
            ) {
              e.target.style.backgroundColor = "#FDD835";
            }
          }}
          onMouseLeave={(e) => {
            if (
              formData.building.trim() &&
              formData.unit.trim() &&
              formData.applicantName.trim()
            ) {
              e.target.style.backgroundColor = "#FEE500";
            }
          }}
        >
          {/* 카카오 로고 이미지 */}
          <img
            src="/kakaoLogo.png"
            alt="카카오 로고"
            style={{
              width: "24px",
              height: "24px",
              objectFit: "contain",
            }}
          />
          카카오 로그인
        </button>
      </motion.div>

      {/* 확인 모달 */}
      <ConfirmInfoModal
        isOpen={showConfirmModal}
        formData={formData}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
      />
    </motion.div>
  );
}

export default KakaoLoginPage;
